import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../API/api';
import { toast } from 'react-toastify';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [authState, setAuthState] = useState({
    isAuthenticated: false,
    token: null,
    user: null,
    role: null,
    expiresAt: null,
    roles: [],
    permissions: [],
  });

  const navigate = useNavigate();
  const [TokenExp, setTokenExp] = useState(null);

  // Logout method
  const logout = useCallback(() => {
    console.log('🔓 Logging out');
    setAuthState({
      isAuthenticated: false,
      token: null,
      user: null,
      role: null,
      expiresAt: null,
      roles: [],
      permissions: [],
    });
    sessionStorage.removeItem('authLocalstorage');
    navigate('/');
  }, [navigate]);

  // 🔐 Token expired handler
  const handleTokenExpired = useCallback(() => {
    toast.error("⏰ Votre session a expiré. Veuillez vous reconnecter.", {
      position: "top-center",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
    // Attendre un peu pour que l'utilisateur voie le message avant la déconnexion
    setTimeout(() => {
      logout();
    }, 2000);
  }, [logout]);

  // Check on mount if session is still valid - only run once
  useEffect(() => {
    const storedAuthState = sessionStorage.getItem('authLocalstorage');
    if (!storedAuthState) {
      return; // No stored state, let ProtectedRoute handle redirect
    }

    try {
      const parsed = JSON.parse(storedAuthState);
      if (!parsed || typeof parsed !== 'object') {
        console.error('❌ Invalid auth state format');
        sessionStorage.removeItem('authLocalstorage');
        return; // Let ProtectedRoute handle redirect
      }
      
      if (!parsed.expiresAt) {
        console.log('🔐 No expiration date found');
        sessionStorage.removeItem('authLocalstorage');
        return; // Let ProtectedRoute handle redirect
      }
      
      const expiresAt = new Date(parsed.expiresAt);
      const now = new Date();

      if (isNaN(expiresAt.getTime()) || expiresAt <= now) {
        console.log('🔐 Token expired on load');
        toast.error("⏰ Votre session a expiré. Veuillez vous reconnecter.", {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        sessionStorage.removeItem('authLocalstorage');
        return; // Let ProtectedRoute handle redirect
      } else {
        // Token is valid, set auth state
        setAuthState(parsed);
        setTokenExp(true);
        console.log('✅ Token valid on load');
      }
    } catch (error) {
      console.error('❌ Failed to parse auth state from sessionStorage', error);
      sessionStorage.removeItem('authLocalstorage');
      // Let ProtectedRoute handle redirect
    }
  }, []); // Only run once on mount - eslint-disable-line react-hooks/exhaustive-deps

  // Auto-check for expiration every 2 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      if (authState.expiresAt) {
        const expiresAt = new Date(authState.expiresAt);
        const now = new Date();
        if (expiresAt <= now) {
          console.log('⏰ Token expired during session');
          handleTokenExpired();
        }
      }
    }, 120000); // check every 2 minutes

    return () => clearInterval(interval);
  }, [authState.expiresAt, handleTokenExpired]);

  // Login method
  const login = (userData, token, expiresAt, role) => {
    try {
      // Validate inputs
      if (!userData || !token) {
        console.error('Login failed: Missing userData or token');
        throw new Error('Invalid login data');
      }

      const newAuthState = {
        isAuthenticated: true,
        user: userData,
        token: token,
        role: role,
        expiresAt: expiresAt,
        roles: [],
        permissions: [],
      };

      // Assign roles immediately based on role ID
      if (role === 1) newAuthState.roles = ['admin', 'user'];
      else if (role === 2) newAuthState.roles = ['user'];
      else if (role === 3) newAuthState.roles = ['admin', 'user', 'super_admin'];
      else if (role === 4) newAuthState.roles = ['user-facture']; // Added user-facture
      else if (role === 5) newAuthState.roles = ['client']; // Added client role

      // Set auth state immediately
      setAuthState(newAuthState);
      sessionStorage.setItem('authLocalstorage', JSON.stringify(newAuthState));
      
      console.log('✅ Login successful, navigating to Dashboard');
      // Use replace: true to prevent back navigation to login page
      navigate('/Dashboard', { replace: true });

      // Try to fetch permissions in background (optional)
      if (userData && userData.id) {
        axios.get(`${API}/permissions/${userData.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
          .then(res => {
            // Update permissions if successful
            const updatedAuthState = {
              ...newAuthState,
              permissions: res.data.permissions || res.data.data?.permissions || []
            };
            setAuthState(updatedAuthState);
            sessionStorage.setItem('authLocalstorage', JSON.stringify(updatedAuthState));
          })
          .catch(err => {
            console.error('Error fetching permissions:', err);
            // Don't block login if permissions fetch fails
          });
      }
    } catch (error) {
      console.error('Error in login function:', error);
      throw error;
    }
  };


  return (
    <AuthContext.Provider value={{ authState, login, logout, setAuthState, TokenExp }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
